import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ManagerSidebar from '../components/Sidebar/ManagerSidebar';

const API_BASE_URL = 'https://ikaze-thouse-backend.vercel.app/api/v1';

const GuardForm = ({ existingGuard, onSuccess }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        gender: 'Male',
        status: 'Active'
    });

    useEffect(() => {
        if (existingGuard) setFormData(existingGuard);
    }, [existingGuard]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = existingGuard?.id 
                ? `${API_BASE_URL}/guards/${existingGuard.id}` 
                : `${API_BASE_URL}/guards`;
            
            const method = existingGuard?.id ? 'put' : 'post';
            
            await axios[method](url, formData);
            
            // Trigger callback if provided
            if (onSuccess) onSuccess();
            
            // Immediately return to dashboard
            navigate('/report_guards'); 
        } catch (err) {
            console.error("Save Error:", err);
        }
    };

    return (
        <div className="page-container">
            <ManagerSidebar />
            
            <div className="form-content">
                <div className="form-card">
                    <header className="form-header">
                        <span className="badge">System Registry</span>
                        <h2 className="form-title">
                            {existingGuard ? 'Update Personnel Profile' : 'Register New Personnel'}
                        </h2>
                        <p className="form-subtitle">Ensure all security credentials are accurate and up to date.</p>
                    </header>

                    <form onSubmit={handleSubmit}>
                        <div className="input-group">
                            <label className="label-text">Full Name</label>
                            <input
                                required
                                type="text"
                                className="styled-input"
                                placeholder="Enter guard name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>

                        <div className="input-group">
                            <label className="label-text">Contact Number</label>
                            <input
                                required
                                type="text"
                                className="styled-input"
                                placeholder="+250 XXX XXX XXX"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            />
                        </div>

                        <div className="select-container">
                            <div className="input-group">
                                <label className="label-text">Gender</label>
                                <select
                                    className="styled-select"
                                    value={formData.gender}
                                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                                >
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                </select>
                            </div>

                            <div className="input-group">
                                <label className="label-text">Employment Status</label>
                                <select
                                    className="styled-select"
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                >
                                    <option value="Active">Active</option>
                                    <option value="On Leave">On Leave</option>
                                    <option value="Terminated">Terminated</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-cancel" onClick={() => navigate('/guard-dashboard')}>
                                Cancel
                            </button>
                            <button type="submit" className="submit-btn">
                                {existingGuard ? 'Save Changes' : 'Confirm Registration'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <style>{`
                :root {
                    --bg-obsidian: #0a0a0a;
                    --card-bg: #121212;
                    --accent-cyan: #06b6d4;
                    --accent-glow: rgba(6, 182, 212, 0.3);
                    --text-main: #e5e7eb;
                    --text-dim: #71717a;
                    --border-subtle: rgba(255, 255, 255, 0.08);
                }

                .page-container {
                    display: flex;
                    background: var(--bg-obsidian);
                    min-height: 100vh;
                    font-family: 'Inter', sans-serif;
                }

                .form-content {
                    flex: 1;
                    
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding: 40px;
                }

                .form-card {
                
                    background: white;
                    width: 100%;
                    max-width: 550px;
                    padding: 48px;
                    border-radius: 16px;
                    border: 1px solid var(--border-subtle);
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
                }

                .form-header {
                    margin-bottom: 32px;
                }

                .badge {
                    background: rgba(6, 182, 212, 0.1);
                    color: var(--accent-cyan);
                    padding: 4px 12px;
                    border-radius: 4px;
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                    border: 1px solid rgba(6, 182, 212, 0.2);
                }

                .form-title {
                    font-size: 28px;
                    font-weight: 800;
                    margin: 16px 0 8px 0;
                    color: #fff;
                    letter-spacing: -0.5px;
                }

                .form-subtitle {
                    color: var(--text-dim);
                    font-size: 14px;
                    line-height: 1.5;
                }

                .input-group {
                    margin-bottom: 24px;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }

                .label-text {
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    color: var(--text-dim);
                }

                .styled-input, .styled-select {
                    background: #faf5f5;
                    border: 1px solid var(--border-subtle);
                    padding: 14px 18px;
                    border-radius: 8px;
                    color: #080808;
                    font-size: 15px;
                    transition: all 0.2s ease;
                    outline: none;
                }

                .styled-input:focus, .styled-select:focus {
                    border-color: var(--accent-cyan);
                    box-shadow: 0 0 0 4px rgba(6, 182, 212, 0.1);
                }

                .select-container {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 20px;
                }

                .form-actions {
                    display: flex;
                    gap: 16px;
                    margin-top: 16px;
                }

                .submit-btn {
                    flex: 2;
                    padding: 16px;
                    background: var(--accent-cyan);
                    color: #000;
                    border: none;
                    border-radius: 8px;
                    font-weight: 700;
                    font-size: 15px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 15px var(--accent-glow);
                }

                .submit-btn:hover {
                    transform: translateY(-2px);
                    filter: brightness(1.1);
                    box-shadow: 0 8px 20px var(--accent-glow);
                }

                .btn-cancel {
                    flex: 1;
                    background: transparent;
                    border: 1px solid var(--border-subtle);
                    color: var(--text-dim);
                    border-radius: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: 0.2s;
                }

                .btn-cancel:hover {
                    background: rgba(255, 255, 255, 0.05);
                    color: #fff;
                }

                .styled-select {
                    appearance: none;
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2371717a'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
                    background-repeat: no-repeat;
                    background-position: right 14px center;
                    background-size: 16px;
                }
            `}</style>
        </div>
    );
};

export default GuardForm;